import { chakra, useColorMode, useToken, HStack, Text } from '@chakra-ui/react';
import { ColorMode } from '@investigator/shared/types';
import {
  getSeverityScoreSegmentFillColor,
  makeSegmentPath,
  SeverityBucketLookup,
} from '@investigator/shared/utils';
import { useMemo } from 'react';

const TOTAL_SEGMENTS = 10;
const DEFAULT_CIRCLE_SIZE = '55px';
const DEFAULT_FONT_SIZE = '24px';
const DEFAULT_OUTSIDE_CIRCLE_SIZE = '27px';
const DEFAULT_OUTSIDE_FONT_SIZE = '15px';

export enum TextPlacement {
  Inside = 'inside',
  Outside = 'outside',
}

export interface SeverityScoreProps {
  score: number;
  dataTestId?: string;
  circleSize?: string;
  fontSize?: string;
  textPlacement?: TextPlacement;
}

export const SeverityScore = ({
  score,
  dataTestId,
  circleSize = DEFAULT_CIRCLE_SIZE,
  fontSize = DEFAULT_FONT_SIZE,
  textPlacement = TextPlacement.Inside,
}: SeverityScoreProps): JSX.Element => {
  const { colorMode } = useColorMode();
  const severityBucket = SeverityBucketLookup[score] || 'unknown';

  // Adjust circle and font sizes based on text placement
  if (textPlacement === TextPlacement.Outside) {
    circleSize =
      circleSize === DEFAULT_CIRCLE_SIZE
        ? DEFAULT_OUTSIDE_CIRCLE_SIZE
        : circleSize; // Only update if default
    fontSize =
      fontSize === DEFAULT_FONT_SIZE ? DEFAULT_OUTSIDE_FONT_SIZE : fontSize; // Only update if default
  }

  const fillColors = useMemo(
    () =>
      // Loop through each segment to determine its fill color
      Array.from({ length: TOTAL_SEGMENTS }).map(
        (_: unknown, index: number) => {
          return getSeverityScoreSegmentFillColor(
            index,
            score,
            severityBucket,
            colorMode as ColorMode,
          );
        },
      ),
    [score, severityBucket, colorMode],
  );

  // Convert color strings to Chakra UI tokens
  const fillColorTokens = useToken('colors', fillColors);

  const renderScoreCircle = (): JSX.Element[] =>
    fillColorTokens.map((resolvedColor: string, index: number) => (
      <path
        key={index}
        d={makeSegmentPath(TOTAL_SEGMENTS + index, TOTAL_SEGMENTS)}
        fill={resolvedColor}
      />
    ));

  return (
    <HStack
      alignItems="center"
      gap={0}
      data-testid={dataTestId || `rank-severity-${severityBucket}`}
    >
      <chakra.svg
        width={circleSize}
        height={circleSize}
        viewBox={'0 0 100 100'}
      >
        {renderScoreCircle()}
        {textPlacement === TextPlacement.Inside && (
          <text
            x="50%"
            y="52%"
            dominantBaseline="middle"
            textAnchor="middle"
            fill="currentColor"
            fontSize={fontSize}
          >
            {score}
          </text>
        )}
      </chakra.svg>
      {textPlacement === TextPlacement.Outside && (
        <Text
          fontSize={fontSize}
          color="currentColor"
          fontWeight={'500'}
          ml={1}
        >
          {score}
        </Text>
      )}
    </HStack>
  );
};
